import _ from 'lodash';
import React, { PureComponent } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import produce from 'immer';
import { StoryMediaQuery } from 'types/story';
import { AdminReducerState } from 'admin/reducers';
import { ActionCreators as UndoActionCreators } from 'redux-undo';
import { CARD_ROOT_ELEMENT_INDEX, COMPONENT_TYPE, PARAMS } from 'common/constants';
import { ADMIN_PREVIEW_PAGE } from 'client/constants/routes';
import { setEditorSize } from 'admin/actions/set-editor-size';
import { setEditorCurrentMediaQuery } from 'admin/actions/set-editor-current-media-query';
import { setMemberPreferences } from 'admin/actions/member/preferences/set-member-preferences';
import MediaBoundariesObserver from 'admin/components/pages/Story/CardEditor/Editor/MediaBoundariesObserver';
import {
	withIframeTransmitter,
	WithIframeTransmitterType,
} from 'admin/components/pages/Story/CardEditor/OtherComponents/IframeTransmitter';
import css from '../CardEditor.scss';
import Resizer from './Resizer';

const { STORY_ID, CARD_ID } = PARAMS;

const getSrc = (storyId, cardId) => `${ADMIN_PREVIEW_PAGE}?${STORY_ID}=${storyId}&${CARD_ID}=${cardId}&editor=true`;
/**
 * @param userPreferences {UserPreferences}
 * @param storyId {string}
 * @return {string}
 */
const getInitialPlatform = (userPreferences, storyId) =>
	(_.get(userPreferences, 'last.storyId') === storyId && _.get(userPreferences, 'editor.platform')) || '';
/**
 * @param userPreferences {UserPreferences}
 * @param storyId {string}
 * @return {string}
 */
const getInitialWidth = (userPreferences, storyId) =>
	(_.get(userPreferences, 'last.storyId') === storyId && _.get(userPreferences, 'editor.width')) || '';

export const IFRAME_ID = 'client-iframe';

type OwnProps = {
	storyId: string;
	cardId: string;
	mediaQuery: {
		defaultPlatform: StoryMediaQuery['defaultPlatform'];
		config: StoryMediaQuery['config'];
	};
};

type Props = ConnectedProps<typeof connector> & OwnProps & WithIframeTransmitterType;

class EditorCanvas extends PureComponent<Props> {
	iframeRef: React.RefObject<HTMLIFrameElement> = React.createRef();

	onIframeResizeThrottle: ReturnType<typeof _.throttle>;

	constructor(props, context) {
		super(props, context);

		this.onIframeResizeThrottle = _.throttle(this.onIframeResize, 100);
	}

	componentDidMount() {
		const platform =
			getInitialPlatform(this.props.userPreferences, this.props.storyId) || this.props.mediaQuery.defaultPlatform;

		if (platform !== this.props.currentMediaQuery) {
			this.props.setEditorCurrentMediaQuery(platform);
		}

		const { currCardId: present, pastCardId: past } = this.props;
		if (past && present && past !== present) {
			this.props.clearHistory();
		}
	}

	componentWillUnmount() {
		this.iframe.removeListener();
	}

	get iframe() {
		const { contentWindow } = this.iframeRef.current!;

		return {
			contentWindow: contentWindow!,
			addListener: () => contentWindow!.addEventListener('resize', this.onIframeResizeThrottle),
			removeListener: () => contentWindow!.removeEventListener('resize', this.onIframeResizeThrottle),
		};
	}

	onIframeLoad = event => {
		event.target.contentDocument.documentElement.setAttribute('data-editor', true);

		this.iframe.addListener();
		this.onIframeResize();
	};

	onIframeResize = () => {
		const {
			contentWindow: { innerWidth: width, innerHeight: height },
		} = this.iframe;
		this.props.setEditorSize({ width, height });
	};

	/**
	 * @param width {string}
	 */
	onWidthChanged = (width: string) => {
		const pref = this.props.userPreferences;

		if (
			pref?.last?.storyId === this.props.storyId &&
			pref?.editor?.platform === this.props.currentMediaQuery &&
			pref?.editor?.width === width
		) {
			return;
		}

		this.props.setMemberPreferences({
			memberId: this.props.memberId,
			preferences: produce(this.props.userPreferences, draft => {
				_.set(draft, 'last.storyId', this.props.storyId);
				_.set(draft, 'editor.width', width);
				_.set(draft, 'editor.platform', this.props.currentMediaQuery);
			}),
		});
	};

	onClick = e => {
		if (e.currentTarget === e.target) {
			this.props.initiateElementSelectInClient(this.props.cardBlockId);
		}
	};

	render() {
		const { currentMediaQuery } = this.props;

		if (!this.props.mediaQuery || !currentMediaQuery || currentMediaQuery === 'default') {
			return null;
		}

		return (
			<div className={css.editorCanvas}>
				<MediaBoundariesObserver
					mediaQuery={this.props.mediaQuery}
					currentMediaQuery={currentMediaQuery}
					setEditorSize={this.props.setEditorSize}
				/>
				<Resizer
					onContainerClick={this.onClick}
					className={css.resizer}
					mediaQuery={this.props.mediaQuery}
					currentMediaQuery={currentMediaQuery}
					onEndChangeSize={this.onWidthChanged}
					initialWidth={getInitialWidth(this.props.userPreferences, this.props.storyId)}
				>
					<iframe
						ref={this.iframeRef}
						onLoad={this.onIframeLoad}
						id={IFRAME_ID}
						className={css.iframe}
						src={getSrc(this.props.storyId, this.props.cardId)}
						frameBorder="0"
						title="preview"
						width="100%"
						height="100%"
					/>
				</Resizer>
			</div>
		);
	}
}

const mapStateToProps = (state: AdminReducerState) => ({
	currentMediaQuery: state.cardEditor.present.currentMediaQuery,
	userPreferences: state.user.preferences,
	memberId: state.user.id,
	cardBlockId: _.get(
		state,
		`cardEditor.present.data.elements.${CARD_ROOT_ELEMENT_INDEX[COMPONENT_TYPE.CARD]}.uiConfig.nodeProps.id`
	),
	pastCardId: _.get(_.last(state.cardEditor.past), 'data._id', '') as string,
	currCardId: _.get(state.cardEditor.present, 'data._id', '') as string,
});

const mapDispatchToProps = {
	setEditorCurrentMediaQuery,
	setMemberPreferences,
	setEditorSize,
	clearHistory: UndoActionCreators.clearHistory,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(withIframeTransmitter(EditorCanvas));
